import { useState, useEffect, useCallback } from 'react';
import { useEnglishAuctions } from '@/hooks/marketplace/useEnglishAuctions';
import { useConnection, useReadContract } from 'wagmi';
import { erc721Abi, type Address } from 'viem';
import { shibaEternityAbi } from '@/constants/abi/shibaEternityAbi';
import { getTokensByChainId } from '@/constants/marketplace/tokens';
import { Button, Input, Select, SelectItem } from '@heroui/react';
import toast from '@/util/toast';
import { getMarketplaceContract } from '@/constants/marketplace/contracts';
import { chainSettings, defaultNativeAddress, ChainType } from '@/constants/chain-specific/chains';
import ApprovalTransactionFlow, { AssetType } from '@/components/shared/ApprovalTransactionFlow';
import { useApprovalFlow } from '@/hooks/shared/useApprovalFlow';
import { getCollectionByAddress } from '@/constants/marketplace/collections';
interface CreateAuctionFormProps {
  chainId: number;
  assetContract: Address;
  tokenId: string;
  onSuccess?: () => void;
}
export default function CreateAuctionForm({
  chainId,
  assetContract,
  tokenId,
  onSuccess
}: CreateAuctionFormProps) {
  const [minimumBid, setMinimumBid] = useState('');
  const [buyoutPrice, setBuyoutPrice] = useState('');
  const [currency, setCurrency] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days from now
  );
  const [timeBuffer, setTimeBuffer] = useState('900'); // 15 minutes in seconds
  const [bidBuffer, setBidBuffer] = useState('500'); // 5% in basis points

  const {
    address
  } = useConnection();
  const {
    createAuction,
    isCreatingAuction
  } = useEnglishAuctions(chainId);
  const {
    checkNeedsApproval,
    executeApproval
  } = useApprovalFlow(chainId);

  // Get marketplace contract address for approval
  const marketplace = getMarketplaceContract(chainId);
  const marketplaceAddress = marketplace?.address as Address;

  // Get collection to check if it's ERC1155
  const collection = getCollectionByAddress(assetContract, chainId as ChainType);
  const isERC1155 = collection?.type === 'ERC1155';

  // Native currency support
  const getNativeCurrency = (chainId_0: number) => {
    const chainConfig = chainSettings[chainId_0 as ChainType];
    if (!chainConfig) return null;
    return {
      address: defaultNativeAddress as Address,
      symbol: chainConfig.nativeCurrency.symbol,
      name: chainConfig.nativeCurrency.name,
      logo: chainConfig.nativeCurrency.logo,
      decimals: chainConfig.nativeCurrency.decimals
    };
  };

  // Get supported tokens for this chain including native currency
  const erc20Tokens = getTokensByChainId(chainId)?.tokens || [];
  const nativeToken = getNativeCurrency(chainId);

  // Combine native currency with ERC20 tokens
  const supportedTokens = nativeToken ? [nativeToken, ...erc20Tokens] : erc20Tokens;
  const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);

  // Check if user owns the NFT and if it's approved
  useEffect(() => {
    const checkOwnershipAndApproval = async () => {
      if (!address || !assetContract || !tokenId) return;
      try {
        // For ERC721, check ownership
        if (!isERC1155) {
          const {
            data: owner
          } = await useReadContract({
            address: assetContract,
            abi: erc721Abi,
            functionName: 'ownerOf',
            args: [BigInt(tokenId)]
          });
          if (owner !== address) {
            toast.error('You do not own this NFT');
            return;
          }
        } else {
          // For ERC1155, check balance
          const {
            data: balance
          } = await useReadContract({
            address: assetContract,
            abi: shibaEternityAbi,
            functionName: 'balanceOf',
            args: [address, BigInt(tokenId)]
          });
          if (!balance || balance === 0n) {
            toast.error('You do not own this token');
            return;
          }
        }
      } catch (err) {
        console.error('Error checking ownership:', err);
      }
    };
    checkOwnershipAndApproval();
  }, [address, assetContract, tokenId, collection, isERC1155]);

  // Set a default currency when supported tokens load
  useEffect(() => {
    if (supportedTokens.length > 0 && !currency) {
      setCurrency(supportedTokens[0].address);
    }
  }, [supportedTokens]);

  // Function to create auction (called after approval if needed)
  const executeAuctionCreation = useCallback(async () => {
    if (!address || !assetContract || !tokenId || !minimumBid || !buyoutPrice || !currency || !startDate || !endDate) {
      toast.error('Please fill in all required fields');
      return {
        success: false
      };
    }
    if (endDate <= startDate) {
      toast.error('End date must be after start date');
      return {
        success: false
      };
    }

    // Validate buyout price is higher than minimum bid
    if (parseFloat(buyoutPrice) <= parseFloat(minimumBid)) {
      toast.error('Buyout price must be higher than minimum bid');
      return {
        success: false
      };
    }
    try {
      const txHash = await createAuction(assetContract, BigInt(tokenId), BigInt(quantity), currency as Address, minimumBid, buyoutPrice, parseInt(timeBuffer), parseInt(bidBuffer), Math.floor(startDate.getTime() / 1000), Math.floor(endDate.getTime() / 1000));
      toast.success('Creating your auction...');
      if (onSuccess) {
        onSuccess();
      }
      return {
        success: true,
        hash: typeof txHash === 'string' ? txHash : undefined
      };
    } catch (err_0) {
      console.error('Error creating auction:', err_0);
      const errorMessage = err_0 instanceof Error ? err_0.message : 'Failed to create auction';
      toast.error(errorMessage);
      return {
        success: false
      };
    }
  }, [address, assetContract, tokenId, minimumBid, buyoutPrice, currency, startDate, endDate, quantity, timeBuffer, bidBuffer, createAuction, onSuccess]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!address) {
      toast.error('Please connect your wallet');
      return;
    }
    if (!assetContract || !tokenId || !minimumBid || !buyoutPrice || !currency || !startDate || !endDate) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!marketplaceAddress) {
      toast.error('Marketplace contract not found for this chain');
      return;
    }

    // Open the approval transaction flow
    setIsApprovalDialogOpen(true);
  };

  // Configure approval text for better user experience
  const approvalConfig = {
    assetType: isERC1155 ? AssetType.ERC1155 : AssetType.ERC721,
    assetAddress: assetContract,
    spenderAddress: marketplaceAddress,
    chainId: chainId,
    tokenId: tokenId ? BigInt(tokenId) : undefined,
    text: {
      modalTitle: {
        approval: `Approve ${isERC1155 ? 'ERC-1155' : 'NFT'} for Marketplace`,
        main: 'Creating Auction',
        complete: 'Auction Created'
      },
      steps: {
        approval: 'Approve',
        main: 'Create Auction'
      },
      messages: {
        checking: 'Checking if approval is needed...',
        needsApproval: `You need to approve this ${isERC1155 ? 'ERC-1155 token' : 'NFT'} before creating an auction`,
        approving: 'Please confirm the approval transaction in your wallet',
        approved: `${isERC1155 ? 'ERC-1155 token' : 'NFT'} approved! Ready to create auction`,
        mainAction: `Creating ${isERC1155 ? 'ERC-1155' : 'NFT'} auction...`,
        complete: `Your ${isERC1155 ? 'ERC-1155 token' : 'NFT'} auction has been created successfully!`
      },
      buttons: {
        approve: `Approve ${isERC1155 ? 'ERC-1155' : 'NFT'}`,
        executeMain: 'Create Auction',
        close: 'Close'
      }
    }
  };
  return <>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-6 bg-neutral-900 p-6 rounded-lg">
          <h2 className="text-2xl font-bold mb-6">Create English Auction</h2>

          <div className="space-y-2">
            <label className="text-sm font-medium">Minimum Bid *</label>
            <div className="flex gap-2">
              <Input type="text" value={minimumBid} onChange={e_0 => setMinimumBid(e_0.target.value)} placeholder="Minimum bid amount" className="flex-1" />

              <Select selectedKeys={currency ? [currency] : []} onSelectionChange={keys => {
              const selected = Array.from(keys)[0];
              setCurrency(selected as string);
            }} placeholder="Select currency" className="w-1/3" renderValue={items => {
              const selectedValue = items[0];
              if (!selectedValue) return null;
              const token = supportedTokens.find(t => t.address === selectedValue.key);
              return <div className="flex items-center gap-2">
                      {token?.logo && <img src={token.logo} alt={token.symbol} className="w-5 h-5 rounded-full" />}
                      <span>{token?.symbol}</span>
                    </div>;
            }}>
                {supportedTokens.map(token_0 => <SelectItem key={token_0.address} textValue={token_0.symbol} startContent={token_0.logo ? <img src={token_0.logo} alt={token_0.symbol} className="w-5 h-5 rounded-full" /> : null}>
                    <div className="flex items-center gap-2">
                      <span>{token_0.symbol}</span>
                      <span className="text-xs text-gray-500">- {token_0.name}</span>
                    </div>
                  </SelectItem>)}
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Buyout Price *</label>
            <Input type="text" value={buyoutPrice} onChange={e_1 => setBuyoutPrice(e_1.target.value)} placeholder="Instant buyout price" />
            <p className="text-sm text-neutral-400">
              Buyers can instantly purchase at this price
            </p>
          </div>

          {isERC1155 && <div className="space-y-2">
              <label className="text-sm font-medium">Quantity *</label>
              <Input type="number" value={quantity} onChange={e_2 => setQuantity(e_2.target.value)} min="1" />
            </div>}

          <div className="space-y-2">
            <label className="text-sm font-medium">Start Date *</label>
            <Input type="datetime-local" value={startDate.toISOString().slice(0, -8)} onChange={e_3 => setStartDate(new Date(e_3.target.value))} min={new Date().toISOString().slice(0, -8)} />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">End Date *</label>
            <Input type="datetime-local" value={endDate.toISOString().slice(0, -8)} onChange={e_4 => setEndDate(new Date(e_4.target.value))} min={startDate.toISOString().slice(0, -8)} />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Time Buffer (seconds)</label>
            <Input type="number" value={timeBuffer} onChange={e_5 => setTimeBuffer(e_5.target.value)} min="0" />
            <p className="text-sm text-neutral-400">
              Auction extends by this many seconds if a bid is placed near the end
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Bid Buffer (%)</label>
            <Input type="number" value={bidBuffer} onChange={e_6 => setBidBuffer(e_6.target.value)} min="0" max="10000" />
            <p className="text-sm text-neutral-400">
              Minimum percentage higher that a new bid must be (in basis points, e.g. 500 = 5%)
            </p>
          </div>

          <Button type="submit" color="primary" className="w-full" disabled={isCreatingAuction}>
            {isCreatingAuction ? 'Creating Auction...' : 'Create Auction'}
          </Button>
        </div>
      </form>

      {/* Approval Transaction Flow Dialog */}
      <ApprovalTransactionFlow isOpen={isApprovalDialogOpen} onClose={() => setIsApprovalDialogOpen(false)} config={approvalConfig} checkNeedsApproval={checkNeedsApproval} executeApproval={executeApproval} executeMainTransaction={executeAuctionCreation} />
    </>;
}